// Questions tab: FieldArray of question cards and add-question button.

import React from "react";
import {
    Button,
    Card,
    CardBody,
    IconButton,
    TabPanel,
    Tooltip,
    Typography,
} from "@material-tailwind/react";
import { FieldArray, useFormikContext } from "formik";
import DeleteQuestionDialog from "components/dialog/delete-question-dialog";
import QuestionSettingsDialog from "components/dialog/question-settings-dialog";
import QuestionAnswerInputs from "components/form/question-answers";
import FormikInput from "components/form/formik-input";
import MaterialIcon from "components/material-icon";
import { isEditable } from "../lifecycle-visibility";

export default function QuestionsPanel({
    survey,
    onAutoSave,
    handleSettingsOpen,
    handleCloneQuestion,
    handleAddQuestion,
    setQuestionChop,
    moveQuestionUp,
    moveQuestionDown,
    showQuestionDeleteDialog,
    setShowQuestionDeleteDialog,
    questionToDelete,
    setQuestionToDelete,
    setQuestionsToDelete,
    showSettingsDialog,
    setShowSettingsDialog,
    questionEditIndex,
}) {
    const { values, setFieldValue } = useFormikContext();
    const readOnly = !isEditable(survey.state);

    return (
        <TabPanel value="questions">
            <FieldArray name="questions">
                {({ push, remove, insert, swap }) => (
                    <>
                        {[...values.questions]
                            .sort((a, b) => {
                                return a.position - b.position;
                            })
                            .map((question, index) => (
                                <Card
                                    key={index}
                                    className="card mb-4 p-0"
                                >
                                    <CardBody className="card-inner-body">
                                        <div className="flex flex-col gap-2">
                                            <div className="flex justify-between gap-2">
                                                <div className="font-display mb-1 min-w-fit font-bold text-black p-2 ">
                                                    Q.
                                                    {index + 1}
                                                </div>
                                                <div className="text-black bg-white font-body px-4 py-2 rounded-2xl">
                                                    <Typography variant="h6">
                                                        Question type:
                                                        <span className="inline ml-2">
                                                            {question.question_type === "mixed"
                                                                ? "Mixed"
                                                                : question.question_type === "freeform"
                                                                    ? "Gobby®"
                                                                    : "Preset Answers"}
                                                        </span>
                                                    </Typography>
                                                </div>
                                                <div>
                                                    <Tooltip
                                                        className="font-body"
                                                        content="Question settings."
                                                        animate={{
                                                            mount: {
                                                                scale: 1,
                                                                y: 0,
                                                            },
                                                            unmount: {
                                                                scale: 0,
                                                                y: 25,
                                                            },
                                                        }}
                                                    >
                                                        <IconButton
                                                            className="btn btn--cta px-8"
                                                            type="button"
                                                            variant="text"
                                                            onClick={() =>
                                                                handleSettingsOpen(index)
                                                            }
                                                        >
                                                            <MaterialIcon
                                                                name="settings"
                                                                size="2xl"
                                                            />
                                                        </IconButton>
                                                    </Tooltip>
                                                </div>
                                            </div>
                                            <div className=" p-2 mt-2 font-body sm:rounded-2xl rounded-xl">
                                                <FormikInput
                                                    name={`questions.${index}.text`}
                                                    type="text"
                                                    color="black"
                                                    label="Please enter your question."
                                                    disabled={readOnly}
                                                />
                                            </div>
                                            {(question.question_type === "fixed" ||
                                                question.question_type === "mixed") && (
                                                <FieldArray
                                                    name={`questions.${index}.preset_answers`}
                                                >
                                                    {(answerArrayHelpers) => (
                                                        <QuestionAnswerInputs
                                                            questionIndex={index}
                                                            pushCallback={answerArrayHelpers.push}
                                                            removeCallback={answerArrayHelpers.remove}
                                                            swapCallback={answerArrayHelpers.swap}
                                                            isReadOnly={readOnly}
                                                        />
                                                    )}
                                                </FieldArray>
                                            )}
                                        </div>
                                        <div className="flex flex-row justify-end">
                                            <div className="rounded-2xl bg-white w-fit p-2 flex flex-row gap-2">
                                                <Tooltip
                                                    className="font-body"
                                                    content="Move this question up one position"
                                                    animate={{
                                                        mount: {
                                                            scale: 1,
                                                            y: 0,
                                                        },
                                                        unmount: {
                                                            scale: 0,
                                                            y: 25,
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        className="btn-icon--secondary"
                                                        type="button"
                                                        variant="text"
                                                        disabled={
                                                            index === 0 ||
                                                            readOnly
                                                        }
                                                        onClick={() =>
                                                            moveQuestionUp(
                                                                swap,
                                                                values,
                                                                index,
                                                                setFieldValue,
                                                            )
                                                        }
                                                    >
                                                        <MaterialIcon
                                                            name="arrow_upward"
                                                            size="xl"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    className="font-body"
                                                    content="Move this question down one position"
                                                    animate={{
                                                        mount: {
                                                            scale: 1,
                                                            y: 0,
                                                        },
                                                        unmount: {
                                                            scale: 0,
                                                            y: 25,
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        className="btn-icon--secondary"
                                                        type="button"
                                                        variant="text"
                                                        disabled={
                                                            index ===
                                                                values.questions.length - 1 ||
                                                            readOnly
                                                        }
                                                        onClick={() =>
                                                            moveQuestionDown(
                                                                swap,
                                                                values,
                                                                index,
                                                                setFieldValue,
                                                            )
                                                        }
                                                    >
                                                        <MaterialIcon
                                                            name="arrow_downward"
                                                            size="xl"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    content="Duplicate this question."
                                                    className="font-body"
                                                    animate={{
                                                        mount: {
                                                            scale: 1,
                                                            y: 0,
                                                        },
                                                        unmount: {
                                                            scale: 0,
                                                            y: 25,
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        className="btn-icon--info"
                                                        type="button"
                                                        variant="text"
                                                        onClick={() =>
                                                            handleCloneQuestion(
                                                                index,
                                                                question,
                                                                insert,
                                                            )
                                                        }
                                                        disabled={readOnly}
                                                    >
                                                        <MaterialIcon
                                                            name="content_copy"
                                                            size="xl"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                                <Tooltip
                                                    className="font-body"
                                                    content="Delete this question."
                                                    animate={{
                                                        mount: {
                                                            scale: 1,
                                                            y: 0,
                                                        },
                                                        unmount: {
                                                            scale: 0,
                                                            y: 25,
                                                        },
                                                    }}
                                                >
                                                    <IconButton
                                                        className="btn-icon--danger"
                                                        type="button"
                                                        variant="text"
                                                        onClick={() =>
                                                            setQuestionChop(index)
                                                        }
                                                        disabled={readOnly}
                                                    >
                                                        <MaterialIcon
                                                            name="delete"
                                                            size="xl"
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </CardBody>
                                </Card>
                            ))}
                        <div className="flex justify-center">
                            <Button
                                className="btn btn--primary"
                                type="button"
                                onClick={() => {
                                    const updated = handleAddQuestion(values, push);
                                    setTimeout(() => onAutoSave(updated), 0);
                                }}
                                variant="filled"
                                disabled={readOnly}
                            >
                                Add Another Question
                            </Button>
                        </div>
                        <DeleteQuestionDialog
                            showDialog={showQuestionDeleteDialog}
                            deleteIndex={questionToDelete}
                            removeCallback={remove}
                            setDeleteArrayState={setQuestionsToDelete}
                            onClose={() =>
                                setQuestionToDelete(undefined)
                            }
                            handleClose={() =>
                                setShowQuestionDeleteDialog(
                                    (showQuestionDeleteDialog) =>
                                        !showQuestionDeleteDialog,
                                )
                            }
                        />
                        <QuestionSettingsDialog
                            showDialog={showSettingsDialog}
                            qI={questionEditIndex}
                            handleClose={() =>
                                setShowSettingsDialog(
                                    (showSettingsDialog) =>
                                        !showSettingsDialog,
                                )
                            }
                            isReadOnly={readOnly}
                        />
                    </>
                )}
            </FieldArray>
        </TabPanel>
    );
}
